import { useMemo } from 'react';
import Introduction from '@/pages/CompanyReport/components/Introduction';
import RecentSectionUpdates from '@/pages/CompanyReport/components/Introduction/components/RecentSectionUpdates';
import CriteriaChecks from '@/pages/CompanyReport/components/Introduction/components/CriteriaChecks';
import KeyInformation from '@/pages/CompanyReport/components/Introduction/components/KeyInformation';

import { useCompany } from '@/hooks/useCompany';
import type { StatementChecks } from '@/pages/CompanyReport/components/Introduction/utils';

import { Heading } from '@simplywallst/ui-core';
import { usePastTranslation } from '@/pages/CompanyReport/components/Past/hooks/usePastTranslation';

import { formatterPercentage } from '@/utilities/format';
import { useDateFormatter } from '@/hooks/useDateFormatter';

interface PastHeaderProps {
  statementChecks: StatementChecks[];
  canonicalUrl: string;
  industryName: string;
}

export const PastHeader = ({
  statementChecks,
  canonicalUrl,
  industryName,
}: PastHeaderProps) => {
  const t = usePastTranslation();
  const { format } = useDateFormatter();

  const { data } = useCompany(canonicalUrl);

  const pastData = data?.data.analysis?.data.extended.data.analysis.past;
  const futureData = data?.data.analysis?.data.extended.data.analysis.future;
  const industryAveragesData =
    data?.data.analysis?.data.extended.data.industry_averages;

  const {
    earningsGrowthRate,
    epsGrowthRate,
    industryGrowthRate,
    revenueGrowthRate,
    returnOnEquity,
    netMargin,
    earningUpdateAt,
  } = useMemo(() => {
    const earningUpdateAt =
      futureData?.next_earnings_release || pastData?.last_earnings_update;

    return {
      earningsGrowthRate: formatterPercentage(
        pastData?.net_income_growth_5y ||
          pastData?.net_income_growth_3y ||
          pastData?.net_income_growth_1y
      ),
      epsGrowthRate: formatterPercentage(
        pastData?.earnings_per_share_growth_5y ||
          pastData?.earnings_per_share_growth_3y ||
          pastData?.earnings_per_share_growth_1y
      ),
      industryGrowthRate: formatterPercentage(
        industryAveragesData?.pastFiveYearGrowth
      ),
      revenueGrowthRate: formatterPercentage(
        pastData?.revenue_growth_5y ||
          pastData?.revenue_growth_3y ||
          pastData?.revenue_growth_1y
      ),
      returnOnEquity: formatterPercentage(pastData?.return_on_equity),
      netMargin: formatterPercentage(pastData?.net_income_margin),
      earningUpdateAt:
        earningUpdateAt !== undefined && earningUpdateAt !== null
          ? format(earningUpdateAt, 'dd MMM yyyy')
          : t('keyInformation.notAvailable'),
    };
  }, [
    format,
    futureData?.next_earnings_release,
    industryAveragesData?.pastFiveYearGrowth,
    pastData?.earnings_per_share_growth_1y,
    pastData?.earnings_per_share_growth_3y,
    pastData?.earnings_per_share_growth_5y,
    pastData?.last_earnings_update,
    pastData?.net_income_growth_1y,
    pastData?.net_income_growth_3y,
    pastData?.net_income_growth_5y,
    pastData?.net_income_margin,
    pastData?.return_on_equity,
    pastData?.revenue_growth_1y,
    pastData?.revenue_growth_3y,
    pastData?.revenue_growth_5y,
    t,
  ]);

  const keyInformation = useMemo(
    () => (
      <KeyInformation
        direction="row"
        heading={
          <Heading as="h3" typography="small">
            {t('keyInformation.heading')}
          </Heading>
        }
        keyMetrics={[
          {
            title: t('keyInformation.earningsGrowth.title'),
            value: earningsGrowthRate,
            tooltip: t('keyInformation.earningsGrowth.tooltip'),
          },
          {
            title: t('keyInformation.epsGrowth.title'),
            value: epsGrowthRate,
            tooltip: t('keyInformation.epsGrowth.tooltip'),
          },
        ]}
        metrics={[
          {
            title: t('keyInformation.industryGrowth.title', {
              industry: industryName,
            }),
            value: industryGrowthRate,
            tooltip: t('keyInformation.industryGrowth.tooltip', {
              industry: industryName,
            }),
          },
          {
            title: t('keyInformation.revenueGrowth.title'),
            value: revenueGrowthRate,
            tooltip: t('keyInformation.revenueGrowth.tooltip'),
          },
          {
            title: t('keyInformation.returnOnEquity.title'),
            value: returnOnEquity,
            tooltip: t('keyInformation.returnOnEquity.tooltip'),
          },
          {
            title: t('keyInformation.netMargin.title'),
            value: netMargin,
            tooltip: t('keyInformation.netMargin.tooltip'),
          },
          {
            title: futureData?.next_earnings_release
              ? t('keyInformation.earnings.next.title')
              : pastData?.last_earnings_update
                ? t('keyInformation.earnings.last.title')
                : t('keyInformation.earnings.title'),
            value: earningUpdateAt,
          },
        ]}
      />
    ),
    [
      t,
      earningsGrowthRate,
      epsGrowthRate,
      industryName,
      industryGrowthRate,
      revenueGrowthRate,
      returnOnEquity,
      netMargin,
      futureData?.next_earnings_release,
      pastData?.last_earnings_update,
      earningUpdateAt,
    ]
  );

  const recentSectionUpdates = useMemo(
    () => (
      <RecentSectionUpdates
        lookup={canonicalUrl}
        title={t('keyInformation.recentUpdates.title')}
        filterByEventCategory="past"
      />
    ),
    [canonicalUrl, t]
  );

  return (
    <Introduction
      scoreName={t('keyInformation.recentUpdates.scoreName')}
      criteriaChecks={
        <CriteriaChecks
          scoreName={t('keyInformation.recentUpdates.scoreName')}
          checks={statementChecks}
        />
      }
      keyInformation={keyInformation}
      recentSectionUpdates={recentSectionUpdates}
    />
  );
};
